import type { Role } from '@il-colpo/engine';

/**
 * The five thieves as flat silhouettes for the screens that are not the building (briefing, team,
 * the board of suspects). The same shapes and colours as the characters of the scene: a rim of
 * the role's colour, the hat and the prop of the trade, and the suit over the head.
 */

/** Rim colour of each role, as design tokens. */
export const ROLE_RIM: Record<Role, string> = {
  mastermind: 'var(--c-neon-cyan)',
  locksmith: 'var(--c-neon-amber)',
  hacker: 'var(--c-neon-green)',
  lookout: 'var(--c-neon-pink)',
  driver: 'var(--c-bp-chalk)',
};

export type Suit = 'spade' | 'heart' | 'diamond' | 'club' | 'star';
export const SUITS: readonly Suit[] = ['spade', 'heart', 'diamond', 'club', 'star'];

/** One of the five suits, centred on (0, 0) in a box about `s` px either way. */
export function SuitMark({ kind, s = 4.2, fill }: { kind: Suit; s?: number; fill: string }) {
  switch (kind) {
    case 'diamond':
      return <polygon points={`0,${-s} ${s * 0.7},0 0,${s} ${-s * 0.7},0`} fill={fill} />;
    case 'heart':
      return (
        <g fill={fill}>
          <circle cx={-s * 0.4} cy={-s * 0.2} r={s * 0.5} />
          <circle cx={s * 0.4} cy={-s * 0.2} r={s * 0.5} />
          <polygon points={`${-s * 0.88},0 ${s * 0.88},0 0,${s}`} />
        </g>
      );
    case 'spade':
      return (
        <g fill={fill}>
          <circle cx={-s * 0.4} cy={s * 0.1} r={s * 0.5} />
          <circle cx={s * 0.4} cy={s * 0.1} r={s * 0.5} />
          <polygon points={`${-s * 0.88},${-s * 0.05} ${s * 0.88},${-s * 0.05} 0,${-s}`} />
          <polygon points={`${-s * 0.2},${s * 0.9} ${s * 0.2},${s * 0.9} 0,${s * 0.2}`} />
        </g>
      );
    case 'club':
      return (
        <g fill={fill}>
          <circle cx={0} cy={-s * 0.45} r={s * 0.45} />
          <circle cx={-s * 0.48} cy={s * 0.15} r={s * 0.45} />
          <circle cx={s * 0.48} cy={s * 0.15} r={s * 0.45} />
          <polygon points={`${-s * 0.18},${s} ${s * 0.18},${s} 0,${s * 0.1}`} />
        </g>
      );
    case 'star': {
      const pts: string[] = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? s : s * 0.45;
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        pts.push(`${(Math.cos(a) * r).toFixed(2)},${(Math.sin(a) * r).toFixed(2)}`);
      }
      return <polygon points={pts.join(' ')} fill={fill} />;
    }
  }
}

const COAT = '#14142c';
const SKIN = '#e9c9a0';

function Hat({ role, rim }: { role: Role; rim: string }) {
  const edge = { stroke: rim, strokeWidth: 1, fill: COAT };
  switch (role) {
    case 'mastermind':
      return (
        <g>
          <ellipse cx="0" cy="-5" rx="11" ry="3" {...edge} />
          <rect x="-6" y="-14" width="12" height="10" rx="3" {...edge} />
          <rect x="-6" y="-7" width="12" height="2" fill={rim} />
        </g>
      );
    case 'locksmith':
      return (
        <g>
          <ellipse cx="0" cy="-6" rx="8" ry="4" {...edge} />
          <polygon points="-3,-5 9,-4 9,-2 -3,-2" {...edge} />
        </g>
      );
    case 'hacker':
      return (
        <g>
          <path d="M-8 -2 A8 8 0 0 1 8 -2" fill="none" stroke={rim} strokeWidth="2" />
          <circle cx="-8" cy="-1" r="3" fill={rim} />
          <circle cx="8" cy="-1" r="3" fill={rim} />
        </g>
      );
    case 'lookout':
      return (
        <g>
          <ellipse cx="1" cy="-7" rx="9" ry="3.2" {...edge} />
          <circle cx="1" cy="-10" r="1.4" fill={rim} />
        </g>
      );
    case 'driver':
      return (
        <g>
          <rect x="-7" y="-9" width="14" height="6" rx="3" {...edge} />
          <circle cx="-3" cy="-3" r="2.4" fill="none" stroke={rim} strokeWidth="1.2" />
          <circle cx="3" cy="-3" r="2.4" fill="none" stroke={rim} strokeWidth="1.2" />
        </g>
      );
  }
}

function Prop({ role, rim }: { role: Role; rim: string }) {
  switch (role) {
    case 'mastermind':
      return <rect x="-1.5" y="-4" width="3" height="12" rx="1.5" fill="#0f3b73" stroke={rim} />;
    case 'locksmith':
      return (
        <g>
          <circle cx="0" cy="2" r="3.2" fill="none" stroke={rim} strokeWidth="1.2" />
          <rect x="-0.5" y="5" width="1" height="5" fill={rim} />
        </g>
      );
    case 'hacker':
      return (
        <g>
          <rect x="-5" y="1" width="10" height="7" rx="1" fill={COAT} stroke={rim} />
          <rect x="-3" y="3" width="6" height="2" fill={rim} opacity="0.8" />
        </g>
      );
    case 'lookout':
      return (
        <g>
          <circle cx="-2" cy="2" r="2" fill={COAT} stroke={rim} />
          <circle cx="2" cy="2" r="2" fill={COAT} stroke={rim} />
        </g>
      );
    case 'driver':
      return <rect x="-2.5" y="0" width="5" height="3" fill={rim} />;
  }
}

/**
 * A thief, feet at the bottom of the box, about 60 units tall. `showSuit` puts the seat's suit
 * over the head. Decorative: the name and the role are always written beside it.
 */
export function RoleFigure({
  role,
  seat = 0,
  size = 96,
  showSuit = true,
  className,
}: {
  role: Role;
  seat?: number;
  /** Height in px. */
  size?: number;
  showSuit?: boolean;
  className?: string;
}) {
  const rim = ROLE_RIM[role];
  const suit = SUITS[seat % SUITS.length] as Suit;
  return (
    <svg
      className={className}
      viewBox="-24 -70 48 76"
      height={size}
      width={(size * 48) / 76}
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="0" cy="0" rx="12" ry="3" fill="#000" opacity="0.35" />
      <rect x="-5.1" y="-14" width="3.2" height="14" fill={COAT} stroke={rim} strokeWidth="0.8" />
      <rect x="1.9" y="-14" width="3.2" height="14" fill={COAT} stroke={rim} strokeWidth="0.8" />
      <polygon
        points="-7,-36 7,-36 9,-14 -9,-14"
        fill={COAT}
        stroke={rim}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <polygon points="-3,-36 3,-36 0,-28" fill={rim} opacity="0.85" />
      <rect x="-0.5" y="-28" width="1" height="14" fill={rim} opacity="0.35" />
      <rect
        x="-9.6"
        y="-34"
        width="3.2"
        height="13"
        rx="1.6"
        fill={COAT}
        stroke={rim}
        strokeWidth="0.8"
      />
      <g transform="translate(8 -34)">
        <rect
          x="-1.6"
          y="0"
          width="3.2"
          height="13"
          rx="1.6"
          fill={COAT}
          stroke={rim}
          strokeWidth="0.8"
        />
        <g transform="translate(0 13)">
          <Prop role={role} rim={rim} />
        </g>
      </g>
      {role === 'lookout' ? (
        <rect x="-6" y="-37" width="12" height="3.5" rx="1.5" fill={rim} />
      ) : null}
      <g transform="translate(0 -41)">
        <circle cx="0" cy="0" r="6.4" fill={SKIN} stroke={rim} strokeWidth="1" />
        <Hat role={role} rim={rim} />
        {role === 'hacker' ? (
          <g fill="none" stroke={COAT} strokeWidth="1">
            <circle cx="-2.6" cy="0" r="2" />
            <circle cx="2.6" cy="0" r="2" />
          </g>
        ) : null}
      </g>
      {showSuit ? (
        <g transform="translate(0 -62)">
          <SuitMark kind={suit} fill={rim} />
        </g>
      ) : null}
    </svg>
  );
}
